import { useCallback } from "react";
import { toast } from "react-toastify";
import { ProjectDocumentForm } from "types/project";
import { ApolloError, gql, useMutation } from "@apollo/client";

export type CreateProjectDocumentsMutationArgs = {
  projectId: string;
  name: string;
  type: string | undefined;
  template: string | undefined;
  option: string | undefined;
  description: string | undefined;
  googleDriveId: string | undefined;
};

export type ProjectDocumentResponse = {
  data: {
    link: string | undefined;
    error: string | undefined;
    details: string | undefined;
  };
};

export const CREATE_PROJECT_DOCUMENT = gql`
  mutation UpsertProjectDocument(
    $projectId: String!,
    $name: String!,
    $type: String,
    $template: String,
    $option: String,
    $description: String,
    $googleDriveId: String,
  ) {
    data: upsert_project_document(
      project_id: $projectId,
      name: $name,
      type: $type,
      template: $template,
      option: $option,
      description: $description,
      google_drive_id: $googleDriveId
    ) {
      link
      error
      details
    }
  }
`;

export const useCreateProjectDocument = () => {
  const [createProjectDocumentMutation, { loading, error, data }] = useMutation<
    ProjectDocumentResponse,
    CreateProjectDocumentsMutationArgs
  >(CREATE_PROJECT_DOCUMENT);

  const createProjectDocument = useCallback(
    async (
      documentData: ProjectDocumentForm,
      projectId: string,
    ) => {
      try {
        await createProjectDocumentMutation({
          variables: {
            projectId: projectId,
            name: documentData.name,
            type: documentData.type,
            template: documentData.template,
            option: documentData.option,
            description: documentData.description,
            googleDriveId: documentData.google_drive_id
          }
        });
      } catch (ex) {
        toast.error((ex as ApolloError)?.message);
      }
    },
    [createProjectDocumentMutation]
  );

  return {
    createProjectDocument,
    loading,
    error,
    response: data?.data,
  };
};
